import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  MapPin,
  MessageSquare,
  RefreshCw,
  Send,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import {
  buildCommunicationMessageFormData,
  createDeliveryAlert,
  createOrGetDeliveryConversation,
  createOrGetSupportConversation,
  fetchConversationMessages,
  generateConversationAIDraft,
  hasCommunicationMedia,
  improveConversationAIMessage,
  recordDriverTimelineGuidance,
  sendConversationMessage,
  sendDirectSupportWhatsAppMessage,
  sendDriverCustomerMessage,
  updateDeliveryAlert,
} from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import MessageBubble from './MessageBubble';
import MediaMessageControls from './MediaMessageControls';
import { DELIVERY_ALERT_TYPES, getAlertDescription, getAlertLabel } from '../../delivery-alerts/alertTypes';

const TARGET_LABELS = {
  driver: 'Motorizado',
  final_customer: 'Cliente',
  store: 'Tienda',
  provider: 'Proveedor',
  context: 'Contexto',
};

const DRAFT_PRESETS = {
  driver: [
    {
      id: 'driver_wait_5_min',
      label: 'Espera 5 min',
      intent: 'wait_5_min',
      instruction: 'Mensaje para el motorizado: pedirle que espere 5 minutos mientras operaciones contacta cliente o tienda.',
    },
    {
      id: 'driver_contacting',
      label: 'Estamos contactando',
      intent: 'driver_hold_contacting',
      instruction: 'Mensaje para el motorizado: confirmar que operaciones esta contactando cliente/tienda y que se mantenga atento.',
    },
    {
      id: 'driver_advance_route',
      label: 'Avanza ruta',
      intent: 'advance_route',
      instruction: 'Mensaje para el motorizado: indicar que avance con la ruta y deje esta entrega pendiente de confirmacion operativa.',
    },
    {
      id: 'driver_request_reference',
      label: 'Pedir referencia',
      intent: 'request_reference',
      instruction: 'Mensaje para el motorizado: explicar que operaciones esta solicitando referencia o ubicacion exacta.',
    },
    {
      id: 'driver_confirm_arrival',
      label: 'Confirmar llegada',
      intent: 'confirm_arrival',
      instruction: 'Mensaje para el motorizado: pedir confirmacion de llegada y pruebas si corresponde.',
    },
    {
      id: 'driver_cancel_with_proof',
      label: 'Cancelar con pruebas',
      intent: 'cancel_with_proof',
      instruction: 'Mensaje para el motorizado: indicar cancelar solo si corresponde por instruccion de operaciones y subir pruebas/fotos/capturas.',
    },
    {
      id: 'driver_cod_payment',
      label: 'COD / pago',
      intent: 'cod_payment',
      instruction: 'Mensaje para el motorizado: revisar cobro contraentrega, pago o evidencia de pago antes de cerrar.',
    },
  ],
  merchant: [
    {
      id: 'merchant_customer_no_answer',
      label: 'Cliente no contesta',
      intent: 'customer_not_answering',
      instruction: 'Mensaje para tienda/proveedor: avisar que el cliente no contesta y pedir apoyo con numero alternativo o referencia.',
    },
    {
      id: 'merchant_location_changed',
      label: 'Cliente cambió ubicación',
      intent: 'customer_location_changed',
      instruction: 'Mensaje para tienda/proveedor: pedir confirmacion de nueva direccion, referencia y si corresponde continuar la entrega.',
    },
    {
      id: 'merchant_alt_phone',
      label: 'Pedir número alternativo',
      intent: 'request_alternate_phone',
      instruction: 'Mensaje para tienda/proveedor: solicitar numero alternativo del cliente final.',
    },
    {
      id: 'merchant_confirm_reference',
      label: 'Confirmar referencia',
      intent: 'request_reference',
      instruction: 'Mensaje para tienda/proveedor: pedir referencia exacta para ayudar al motorizado.',
    },
    {
      id: 'merchant_driver_waiting',
      label: 'Motorizado esperando',
      intent: 'driver_waiting',
      instruction: 'Mensaje para tienda/proveedor: avisar que el motorizado esta esperando y pedir respuesta rapida.',
    },
    {
      id: 'merchant_resolve_cod',
      label: 'Resolver COD',
      intent: 'cod_payment',
      instruction: 'Mensaje para tienda/proveedor: pedir confirmacion sobre pago contraentrega, monto o evidencia.',
    },
    {
      id: 'merchant_authorize_cancel',
      label: 'Autorizar cancelación',
      intent: 'authorize_cancellation',
      instruction: 'Mensaje para tienda/proveedor: pedir autorizacion o instruccion clara antes de cancelar la entrega.',
    },
  ],
  final_customer: [
    {
      id: 'customer_driver_on_way',
      label: 'Motorizado en camino',
      intent: 'driver_on_way',
      instruction: 'Mensaje para cliente final: avisar que el motorizado esta en camino y pedir que este atento.',
    },
    {
      id: 'customer_driver_arrived',
      label: 'Motorizado llegó',
      intent: 'driver_arrived',
      instruction: 'Mensaje para cliente final: avisar que el motorizado ya llego o esta en la direccion y pedir confirmacion.',
    },
    {
      id: 'customer_confirm_receipt',
      label: 'Confirmar recepción',
      intent: 'confirm_receipt',
      instruction: 'Mensaje para cliente final: confirmar si puede recibir ahora la entrega.',
    },
    {
      id: 'customer_send_location',
      label: 'Enviar ubicación',
      intent: 'request_reference',
      instruction: 'Mensaje para cliente final: pedir ubicacion actual o referencia exacta.',
    },
    {
      id: 'customer_prepare_payment',
      label: 'Preparar pago',
      intent: 'cod_payment',
      instruction: 'Mensaje para cliente final: recordar preparar pago contraentrega si aplica.',
    },
    {
      id: 'customer_last_attempt',
      label: 'Último intento',
      intent: 'last_attempt',
      instruction: 'Mensaje para cliente final: avisar que es el ultimo intento de contacto antes de que operaciones decida siguiente paso.',
    },
    {
      id: 'customer_reschedule',
      label: 'Reprogramar',
      intent: 'reschedule_delivery',
      instruction: 'Mensaje para cliente final: preguntar si necesita reprogramar y pedir confirmacion.',
    },
  ],
};

const GUIDANCE_ACTIONS = [
  ['reviewed', 'Marcar revisado'],
  ['customer_notified', 'Cliente avisado'],
  ['customer_waiting', 'Cliente espera'],
  ['customer_no_answer', 'Cliente no contesta'],
  ['store_notified', 'Tienda avisada'],
  ['store_no_answer', 'Tienda no contesta'],
];

const ADMIN_ALERT_TYPE_VALUES = [
  'driver_action_required',
  'store_action_required',
  'provider_action_required',
  'pricing_review_needed',
  'cod_risk',
  'delivery_priority',
  'incorrect_location',
  'customer_no_answer',
  'address_incomplete',
  'payment_transfer_pending',
  'package_issue',
  'other',
];

const ALERT_TARGET_OPTIONS = [
  { value: 'driver', label: 'Motorizado' },
  { value: 'store', label: 'Tienda' },
  { value: 'provider', label: 'Proveedor' },
];

const ALERT_MESSAGE_SUGGESTIONS = {
  driver_action_required: {
    driver: 'Necesitamos una confirmacion operativa en esta entrega. Revisa la indicacion y responde antes de continuar.',
    store: 'Estamos pidiendo una confirmacion al motorizado para resolver esta entrega. Te avisamos apenas tengamos respuesta.',
    provider: 'Operaciones esta coordinando una accion con el motorizado para esta entrega.',
  },
  store_action_required: {
    driver: 'Mantente atento: operaciones esta solicitando confirmacion a la tienda antes de cerrar esta entrega.',
    store: 'Necesitamos tu confirmacion para continuar con esta entrega. Por favor responde con la instruccion correcta.',
    provider: 'La tienda debe confirmar informacion para desbloquear esta entrega.',
  },
  provider_action_required: {
    driver: 'Operaciones esta escalando esta entrega al proveedor. Espera instruccion antes de tomar una decision final.',
    store: 'Estamos escalando esta entrega al proveedor para resolver la decision operativa.',
    provider: 'Necesitamos tu apoyo para resolver esta entrega. Confirma la instruccion para motorizado/tienda.',
  },
  pricing_review_needed: {
    driver: 'Esta entrega tiene precio/condicion especial registrada. No cierres con monto diferente sin confirmacion.',
    store: 'Esta entrega tiene precio o adicional especial. Verifica el monto antes de confirmar con tu cliente.',
    provider: 'Esta entrega tiene pricing especial o ajuste operativo. Revisar para evitar diferencias de conciliacion.',
  },
  cod_risk: {
    driver: 'Atencion COD: valida monto, evidencia y forma de pago antes de entregar o cerrar.',
    store: 'Tenemos riesgo con el cobro de esta entrega. Confirma monto, pago o autorizacion antes de continuar.',
    provider: 'Riesgo COD detectado. Revisar monto/evidencia para conciliacion y decision operativa.',
  },
  delivery_priority: {
    driver: 'Entrega con prioridad operativa. Mantennos informado de avance y cualquier bloqueo.',
    store: 'Marcamos esta entrega como prioridad operativa para seguimiento cercano.',
    provider: 'Entrega marcada con prioridad operativa para seguimiento del equipo Fast Max.',
  },
  customer_no_answer: {
    driver: 'Cliente no contesta. Espera instruccion de operaciones y registra evidencia de llamadas/mensajes.',
    store: 'El cliente no contesta. Ayudanos con numero alternativo, referencia o instruccion de reprogramacion/cancelacion.',
    provider: 'Cliente no contesta en esta entrega. Se requiere apoyo de tienda/proveedor para desbloquear.',
  },
  incorrect_location: {
    driver: 'La ubicacion parece incorrecta. No te alejes hasta que operaciones confirme direccion o referencia.',
    store: 'La direccion/pin parece incorrecto. Confirma referencia exacta o ubicacion actual con tu cliente.',
    provider: 'Alerta de ubicacion incorrecta. Necesitamos referencia o autorizacion para siguiente paso.',
  },
};

function compactError(err, fallback) {
  const payload = err?.response?.data;
  return payload?.message || payload?.error || err?.message || fallback;
}

function deliveryPrefix(stop) {
  return stop?.delivery_point_id ? `Entrega #${stop.delivery_point_id}:` : 'Entrega:';
}

function defaultChannelFor(target) {
  if (!target || target.contextOnly) return 'internal';
  if (target.target_type === 'final_customer') {
    return target.whatsapp_available ? 'whatsapp' : 'fastmax_app';
  }
  return target.whatsapp_available ? 'whatsapp' : 'internal';
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  return `S/ ${numeric.toFixed(2)}`;
}

function formatAlertTime(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
    }).format(new Date(value));
  } catch (_) {
    return '';
  }
}

function alertSeverityClasses(severity) {
  if (severity === 'high') {
    return {
      card: 'border-red-200 bg-red-50',
      badge: 'bg-red-600 text-white',
      soft: 'bg-red-100 text-red-700',
    };
  }
  if (severity === 'low') {
    return {
      card: 'border-slate-200 bg-slate-50',
      badge: 'bg-slate-600 text-white',
      soft: 'bg-slate-100 text-slate-700',
    };
  }
  return {
    card: 'border-amber-200 bg-amber-50',
    badge: 'bg-amber-500 text-white',
    soft: 'bg-amber-100 text-amber-700',
  };
}

function guidanceStateClasses(status) {
  if (status === 'ready') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'blocked') return 'border-red-200 bg-red-50 text-red-700';
  if (status === 'needs_store') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (status === 'needs_customer') return 'border-sky-200 bg-sky-50 text-sky-700';
  return 'border-slate-200 bg-white text-slate-600';
}

function guidanceActionIsActive(action, state) {
  if (!state) return false;
  if (action === 'reviewed') return Boolean(state.reviewed);
  if (action === 'customer_notified') return state.customer_contact_status === 'notified';
  if (action === 'customer_waiting') return state.customer_contact_status === 'waiting';
  if (action === 'customer_no_answer') return state.customer_contact_status === 'no_answer';
  if (action === 'store_notified') return state.store_contact_status === 'notified';
  if (action === 'store_no_answer') return state.store_contact_status === 'no_answer';
  return false;
}

function draftPresetGroupForTarget(target) {
  if (!target || target.contextOnly) return 'driver';
  if (target.target_type === 'final_customer' || target.customer) return 'final_customer';
  if (target.target_type === 'store' || target.target_type === 'provider' || target.key === 'store' || target.key === 'provider') {
    return 'merchant';
  }
  return 'driver';
}

function draftPresetsForTarget(target) {
  return DRAFT_PRESETS[draftPresetGroupForTarget(target)] || DRAFT_PRESETS.driver;
}

function defaultDraftPresetForTarget(target) {
  return draftPresetsForTarget(target)[0] || null;
}

function activeAlertFromSummary(summary) {
  if (!summary?.active_count) return null;
  return summary.latest_alert || summary.alerts?.[0] || null;
}

function summarizeLocalAlerts(alerts = []) {
  const activeAlerts = alerts.filter(alert => alert.status === 'open' || alert.status === 'acknowledged');
  const severityRank = { low: 1, medium: 2, high: 3, critical: 4 };
  const highest = activeAlerts.reduce((current, alert) => (
    (severityRank[alert.severity] || 0) > (severityRank[current] || 0) ? alert.severity : current
  ), null);
  const openAlerts = activeAlerts.filter(alert => alert.status === 'open');
  const acknowledgedAlerts = activeAlerts.filter(alert => alert.status === 'acknowledged');
  return {
    active_count: activeAlerts.length,
    open_count: openAlerts.length,
    acknowledged_count: acknowledgedAlerts.length,
    unacknowledged_count: openAlerts.length,
    high_count: activeAlerts.filter(alert => alert.severity === 'high').length,
    medium_count: activeAlerts.filter(alert => alert.severity === 'medium').length,
    low_count: activeAlerts.filter(alert => alert.severity === 'low').length,
    highest_severity: highest,
    types: [...new Set(activeAlerts.map(alert => alert.alert_type).filter(Boolean))],
    has_unacknowledged: Boolean(openAlerts.length),
    latest_alert: activeAlerts[0] || null,
    alerts: activeAlerts,
  };
}

function applyUpdatedAlertToSummary(summary, updatedAlert) {
  const existingAlerts = summary?.alerts?.length
    ? summary.alerts
    : summary?.latest_alert
      ? [summary.latest_alert]
      : [];
  const withoutUpdated = existingAlerts.filter(alert => alert.id !== updatedAlert.id);
  const nextAlerts = ['open', 'acknowledged'].includes(updatedAlert.status)
    ? [updatedAlert, ...withoutUpdated]
    : withoutUpdated;
  return summarizeLocalAlerts(nextAlerts);
}

function defaultAdminAlertDraft(stop = {}) {
  return {
    alert_type: stop?.pricing_flags?.is_enhanced ? 'pricing_review_needed' : 'driver_action_required',
    target_audience: ['driver'],
    message: '',
  };
}

function adminAlertTypeOptions(stop = {}) {
  const options = DELIVERY_ALERT_TYPES.filter(item => ADMIN_ALERT_TYPE_VALUES.includes(item.value));
  if (stop?.pricing_flags?.is_enhanced) {
    return [
      ...options.filter(item => item.value === 'pricing_review_needed'),
      ...options.filter(item => item.value !== 'pricing_review_needed'),
    ];
  }
  return options;
}

function availableAlertTargets(localTargets = []) {
  const keys = new Set(localTargets.map(target => target.key));
  return ALERT_TARGET_OPTIONS.filter(option => keys.has(option.value));
}

function alertSuggestionsFor(type, targetAudience = []) {
  const suggestions = ALERT_MESSAGE_SUGGESTIONS[type] || {};
  return targetAudience
    .map(targetType => ({
      targetType,
      text: suggestions[targetType],
    }))
    .filter(item => item.text);
}

function buildTargets(stop, driver) {
  if (!stop) return [];
  const targets = [];
  const driverTarget = stop.driver_target || driver?.driver_target || null;
  if (driverTarget?.user_id || driver?.driver_id) {
    targets.push({
      key: 'driver',
      target_type: 'driver',
      scope: 'driver',
      user_id: driverTarget?.user_id || driver?.driver_id,
      label: driverTarget?.label || driver?.driver_name || 'Motorizado',
      support_conversation_id: driverTarget?.support_conversation_id || null,
      whatsapp_available: Boolean(driverTarget?.whatsapp_available),
      whatsapp_disabled_reason: driverTarget?.whatsapp_disabled_reason,
      phone_number: driverTarget?.phone_number || driver?.phone_number,
    });
  }

  const customerStatus = stop.customer_channel_status || {};
  targets.push({
    key: 'final_customer',
    target_type: 'final_customer',
    label: stop.recipient_name || 'Cliente final',
    customer: true,
    whatsapp_available: Boolean(customerStatus.whatsapp_available),
    whatsapp_disabled_reason: customerStatus.whatsapp_disabled_reason,
    phone_number: customerStatus.phone_number,
    chat_id: customerStatus.chat_id,
  });

  const storeTarget = stop.store_target || null;
  if (storeTarget?.user_id) {
    targets.push({
      key: 'store',
      target_type: storeTarget.target_type || 'store',
      scope: storeTarget.scope || 'store',
      support_target_type: storeTarget.support_target_type || storeTarget.scope || 'store',
      user_id: storeTarget.user_id,
      label: storeTarget?.label || stop.store_name || 'Tienda',
      support_conversation_id: storeTarget?.support_conversation_id || null,
      whatsapp_available: Boolean(storeTarget?.whatsapp_available),
      whatsapp_disabled_reason: storeTarget?.whatsapp_disabled_reason,
      phone_number: storeTarget?.phone_number,
    });
  }

  const providerTarget = stop.provider_target || null;
  if (providerTarget?.user_id) {
    targets.push({
      key: 'provider',
      target_type: 'provider',
      scope: providerTarget.scope || 'provider',
      support_target_type: providerTarget.support_target_type || providerTarget.scope || 'provider',
      user_id: providerTarget.user_id,
      label: providerTarget?.label || stop.provider_name || 'Proveedor',
      support_conversation_id: providerTarget?.support_conversation_id || null,
      whatsapp_available: Boolean(providerTarget?.whatsapp_available),
      whatsapp_disabled_reason: providerTarget?.whatsapp_disabled_reason,
      phone_number: providerTarget?.phone_number,
    });
  }

  targets.push({
    key: 'context',
    target_type: 'context',
    label: 'Contexto',
    contextOnly: true,
  });

  return targets;
}

export default function RouteGuidanceMiniChat({
  stop,
  driver,
  initialTargetKey = null,
  onOpenFullDelivery,
  onOpenFullSupport,
  onAlertUpdated,
  onGuidanceUpdated,
  isOpening = false,
}) {
  const builtTargets = useMemo(() => buildTargets(stop, driver), [stop, driver]);
  const initialSelectedTarget = (
    builtTargets.find(target => target.key === initialTargetKey)
    || builtTargets.find(target => !target.contextOnly)
    || builtTargets[0]
    || null
  );
  const [localTargets, setLocalTargets] = useState(builtTargets);
  const [selectedKey, setSelectedKey] = useState(initialSelectedTarget?.key || 'context');
  const [selectedIntent, setSelectedIntent] = useState('customer_not_answering');
  const [selectedDraftPresetId, setSelectedDraftPresetId] = useState(
    defaultDraftPresetForTarget(initialSelectedTarget)?.id || null
  );
  const [conversationIds, setConversationIds] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState(defaultChannelFor(initialSelectedTarget));
  const [draft, setDraft] = useState('');
  const [mediaAttachment, setMediaAttachment] = useState(null);
  const [aiDraftMeta, setAiDraftMeta] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isAIWorking, setIsAIWorking] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isUpdatingAlert, setIsUpdatingAlert] = useState(false);
  const [isUpdatingGuidance, setIsUpdatingGuidance] = useState(false);
  const [alertCreatorOpen, setAlertCreatorOpen] = useState(false);
  const [alertDraft, setAlertDraft] = useState(() => defaultAdminAlertDraft(stop));
  const [isCreatingAlert, setIsCreatingAlert] = useState(false);
  const [localAlertSummary, setLocalAlertSummary] = useState(stop?.alert_summary || null);
  const [localGuidanceState, setLocalGuidanceState] = useState(stop?.guidance_state || null);
  const [error, setError] = useState(null);
  const selectionResetRef = useRef(null);
  const selectedKeyRef = useRef(selectedKey);
  const loadRequestRef = useRef(0);

  useEffect(() => {
    setLocalTargets(prev => builtTargets.map(target => {
      const current = prev.find(item => item.key === target.key);
      const cachedConversationId = conversationIds[target.key];
      return {
        ...target,
        support_conversation_id: (
          target.support_conversation_id
          || current?.support_conversation_id
          || (!target.customer ? cachedConversationId : null)
        ),
      };
    }));
    setSelectedKey(prev => (
      builtTargets.some(target => target.key === prev)
        ? prev
        : (builtTargets.find(target => !target.contextOnly)?.key || 'context')
    ));
  }, [builtTargets, conversationIds]);

  useEffect(() => {
    const resetKey = `${stop?.delivery_point_id || 'none'}:${initialTargetKey || 'auto'}`;
    if (selectionResetRef.current === resetKey) return;
    selectionResetRef.current = resetKey;

    const firstKey = (
      builtTargets.find(target => target.key === initialTargetKey)?.key
      || builtTargets.find(target => !target.contextOnly)?.key
      || 'context'
    );
    const firstTarget = builtTargets.find(target => target.key === firstKey);
    const firstPreset = defaultDraftPresetForTarget(firstTarget);
    setSelectedKey(firstKey);
    setSelectedDraftPresetId(firstPreset?.id || null);
    setSelectedIntent(firstPreset?.intent || 'customer_not_answering');
    setChannel(defaultChannelFor(firstTarget));
    setConversationIds({});
    setActiveConversationId(null);
    setMessages([]);
    setDraft('');
    setAiDraftMeta(null);
    setAlertCreatorOpen(false);
    setAlertDraft(defaultAdminAlertDraft({ pricing_flags: { is_enhanced: stop?.pricing_flags?.is_enhanced } }));
    setError(null);
  }, [builtTargets, initialTargetKey, stop?.delivery_point_id, stop?.pricing_flags?.is_enhanced]);

  useEffect(() => {
    selectedKeyRef.current = selectedKey;
  }, [selectedKey]);

  useEffect(() => {
    setLocalAlertSummary(stop?.alert_summary || null);
  }, [stop?.delivery_point_id, stop?.alert_summary]);

  useEffect(() => {
    setLocalGuidanceState(stop?.guidance_state || null);
  }, [stop?.delivery_point_id, stop?.guidance_state]);

  const selectedTarget = useMemo(
    () => localTargets.find(target => target.key === selectedKey) || localTargets[0] || null,
    [localTargets, selectedKey]
  );
  const draftPresets = useMemo(() => draftPresetsForTarget(selectedTarget), [selectedTarget]);
  const selectedDraftPreset = useMemo(() => (
    selectedDraftPresetId
      ? (draftPresets.find(preset => preset.id === selectedDraftPresetId) || null)
      : null
  ), [draftPresets, selectedDraftPresetId]);
  const alertTypeOptions = useMemo(() => adminAlertTypeOptions(stop), [stop]);
  const alertTargetOptions = useMemo(() => availableAlertTargets(localTargets), [localTargets]);
  const alertMessageSuggestions = useMemo(
    () => alertSuggestionsFor(alertDraft.alert_type, alertDraft.target_audience),
    [alertDraft.alert_type, alertDraft.target_audience]
  );

  useEffect(() => {
    const allowed = new Set(alertTargetOptions.map(option => option.value));
    setAlertDraft(prev => {
      const filtered = (prev.target_audience || []).filter(item => allowed.has(item));
      if (filtered.length === (prev.target_audience || []).length && filtered.length > 0) return prev;
      return {
        ...prev,
        target_audience: filtered.length ? filtered : (alertTargetOptions[0] ? [alertTargetOptions[0].value] : []),
      };
    });
  }, [alertTargetOptions]);

  function selectTarget(target) {
    const nextPreset = defaultDraftPresetForTarget(target);
    setSelectedKey(target.key);
    setSelectedDraftPresetId(nextPreset?.id || null);
    setSelectedIntent(nextPreset?.intent || 'customer_not_answering');
    setChannel(defaultChannelFor(target));
    setDraft('');
    setAiDraftMeta(null);
    setError(null);
  }

  function selectDraftPreset(preset) {
    setSelectedDraftPresetId(preset?.id || null);
    setSelectedIntent(preset?.intent || 'general_question');
    setAiDraftMeta(null);
  }

  function selectDraftPresetById(presetId, fallbackIntent = null) {
    const preset = draftPresets.find(item => item.id === presetId);
    if (preset) {
      selectDraftPreset(preset);
      return;
    }
    if (fallbackIntent) {
      setSelectedDraftPresetId(null);
      setSelectedIntent(fallbackIntent);
      setAiDraftMeta(null);
    }
  }

  function updateTargetConversationId(targetKey, conversationId) {
    setConversationIds(prev => ({ ...prev, [targetKey]: conversationId }));
    setLocalTargets(prev => prev.map(target => (
      target.key === targetKey
        ? { ...target, support_conversation_id: target.customer ? target.support_conversation_id : conversationId }
        : target
    )));
  }

  const ensureConversation = useCallback(async (target = selectedTarget) => {
    if (!target || target.contextOnly) return null;
    if (conversationIds[target.key]) return conversationIds[target.key];

    if (target.customer) {
      const conversation = await createOrGetDeliveryConversation(stop.delivery_point_id);
      const conversationId = conversation?.id || null;
      if (conversationId) updateTargetConversationId(target.key, conversationId);
      return conversationId;
    }

    const existingId = target.support_conversation_id;
    if (existingId) {
      updateTargetConversationId(target.key, existingId);
      return existingId;
    }
    if (!target.scope || !target.user_id) return null;
    const conversation = await createOrGetSupportConversation(target.scope, target.user_id);
    const conversationId = conversation?.id || null;
    if (conversationId) updateTargetConversationId(target.key, conversationId);
    return conversationId;
  }, [conversationIds, selectedTarget, stop?.delivery_point_id]);

  const loadMessages = useCallback(async (target = selectedTarget) => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    const targetKey = target?.key || null;
    const isCurrentRequest = () => (
      loadRequestRef.current === requestId
      && selectedKeyRef.current === targetKey
    );

    if (!target || target.contextOnly) {
      setActiveConversationId(null);
      setMessages([]);
      return;
    }
    setIsLoadingMessages(true);
    setError(null);
    try {
      const conversationId = await ensureConversation(target);
      if (!isCurrentRequest()) return;
      setActiveConversationId(conversationId);
      if (!conversationId) {
        setMessages([]);
        return;
      }
      const data = await fetchConversationMessages(conversationId, {
        currentUserType: 'admin',
      });
      if (!isCurrentRequest()) return;
      setMessages((data.messages || []).slice(-5));
    } catch (err) {
      if (!isCurrentRequest()) return;
      setError(compactError(err, 'No se pudieron cargar los mensajes recientes.'));
      setMessages([]);
    } finally {
      if (isCurrentRequest()) {
        setIsLoadingMessages(false);
      }
    }
  }, [ensureConversation, selectedTarget]);

  useEffect(() => {
    loadMessages(selectedTarget);
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeConversationId) return undefined;
    const payload = socketAuthPayload({ conversation_id: activeConversationId });
    const join = () => socket.emit('join_conversation', payload);
    const handleMessage = (eventPayload) => {
      const message = eventPayload?.message || eventPayload;
      if (Number(message?.conversation_id) !== Number(activeConversationId)) return;
      setMessages(prev => {
        if (prev.some(item => item.id === message.id && item.channel === message.channel)) return prev;
        return [...prev, message].slice(-5);
      });
    };
    const refresh = (eventPayload = null) => {
      if (eventPayload?.conversation_id && Number(eventPayload.conversation_id) !== Number(activeConversationId)) return;
      loadMessages(selectedTarget);
    };
    const rejoinAndRefresh = () => {
      join();
      loadMessages(selectedTarget);
    };

    join();
    socket.on('connect', rejoinAndRefresh);
    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_action_completed', refresh);
    return () => {
      socket.off('connect', rejoinAndRefresh);
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_action_completed', refresh);
      socket.emit('leave_conversation', payload);
    };
  }, [activeConversationId, loadMessages, selectedTarget]);

  const aiPayload = useCallback((target = selectedTarget) => ({
    preview_only: true,
    source: 'driver_timeline',
    delivery_point_id: stop?.delivery_point_id,
    target_type: target?.target_type,
    intent_hint: selectedDraftPreset?.intent || selectedIntent || undefined,
    draft_preset_id: selectedDraftPreset?.id,
    draft_preset_label: selectedDraftPreset?.label,
    draft_preset_instruction: selectedDraftPreset?.instruction,
    draft_preset_target: draftPresetGroupForTarget(target),
  }), [selectedDraftPreset, selectedIntent, selectedTarget, stop?.delivery_point_id]);

  async function handleGenerateAIDraft() {
    if (!selectedTarget || selectedTarget.contextOnly || isAIWorking) return;
    setIsAIWorking(true);
    setError(null);
    try {
      const conversationId = await ensureConversation(selectedTarget);
      setActiveConversationId(conversationId);
      if (!conversationId) throw new Error('missing_conversation');
      const result = await generateConversationAIDraft(conversationId, aiPayload(selectedTarget));
      const nextDraft = result?.draft;
      const nextText = (nextDraft?.draft_text || '').trim();
      if (!nextText) throw new Error('AI no devolvio un borrador utilizable.');
      setDraft(nextText);
      setAiDraftMeta(nextDraft);
      setChannel(defaultChannelFor(selectedTarget));
    } catch (err) {
      setError(compactError(err, 'No se pudo generar el borrador AI.'));
    } finally {
      setIsAIWorking(false);
    }
  }

  async function handleImproveDraft() {
    const text = draft.trim();
    if (!text || !selectedTarget || selectedTarget.contextOnly || isAIWorking) return;
    setIsAIWorking(true);
    setError(null);
    try {
      const conversationId = await ensureConversation(selectedTarget);
      setActiveConversationId(conversationId);
      if (!conversationId) throw new Error('missing_conversation');
      const result = await improveConversationAIMessage(conversationId, text, aiPayload(selectedTarget));
      setDraft(result.content || text);
      setAiDraftMeta(null);
    } catch (err) {
      setError(compactError(err, 'No se pudo corregir el texto.'));
    } finally {
      setIsAIWorking(false);
    }
  }

  async function handleSend(event) {
    event.preventDefault();
    const text = draft.trim();
    if ((!text && !mediaAttachment) || !selectedTarget || selectedTarget.contextOnly || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const conversationId = await ensureConversation(selectedTarget);
      setActiveConversationId(conversationId);
      if (!conversationId) throw new Error('missing_conversation');
      const basePayload = {
        message_type: mediaAttachment?.type || 'text',
        media_file: mediaAttachment?.file,
        media_type: mediaAttachment?.type,
      };

      if (selectedTarget.customer) {
        const payload = {
          ...basePayload,
          content: text,
          channel: channel === 'whatsapp' ? 'whatsapp' : 'fastmax_app',
          source: 'driver_timeline',
          target_type: 'final_customer',
          delivery_point_id: stop.delivery_point_id,
          intent_hint: selectedIntent,
        };
        await sendDriverCustomerMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
        );
      } else if (channel === 'whatsapp') {
        if (!selectedTarget.whatsapp_available) {
          throw new Error(selectedTarget.whatsapp_disabled_reason || 'WhatsApp no disponible.');
        }
        const payload = {
          ...basePayload,
          content: `${deliveryPrefix(stop)} ${text}`,
          delivery_point_id: stop.delivery_point_id,
          target_type: selectedTarget.support_target_type || selectedTarget.scope || selectedTarget.target_type,
          display_target_type: selectedTarget.target_type,
          support_target_type: selectedTarget.support_target_type || selectedTarget.scope || selectedTarget.target_type,
          source: 'driver_timeline',
          intent_hint: selectedIntent,
        };
        await sendDirectSupportWhatsAppMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
        );
      } else {
        const payload = {
          ...basePayload,
          content: `${deliveryPrefix(stop)} ${text}`,
          source: 'driver_timeline',
          target_type: selectedTarget.target_type,
          delivery_point_id: stop.delivery_point_id,
          intent_hint: selectedIntent,
        };
        await sendConversationMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
        );
      }
      setDraft('');
      setMediaAttachment(null);
      setAiDraftMeta(null);
      await loadMessages(selectedTarget);
    } catch (err) {
      setError(compactError(err, 'No se pudo enviar el mensaje.'));
    } finally {
      setIsSending(false);
    }
  }

  async function handleAlertAction(action) {
    const alertItem = activeAlertFromSummary(localAlertSummary);
    if (!alertItem?.id || isUpdatingAlert) return;
    setIsUpdatingAlert(true);
    setError(null);
    try {
      const updated = await updateDeliveryAlert(alertItem.id, action);
      setLocalAlertSummary(prev => applyUpdatedAlertToSummary(prev, updated));
      onAlertUpdated?.(updated);
    } catch (err) {
      setError(compactError(err, 'No se pudo actualizar la alerta.'));
    } finally {
      setIsUpdatingAlert(false);
    }
  }

  function toggleAlertTarget(targetType) {
    setAlertDraft(prev => {
      const current = new Set(prev.target_audience || []);
      if (current.has(targetType)) current.delete(targetType);
      else current.add(targetType);
      return {
        ...prev,
        target_audience: Array.from(current),
      };
    });
  }

  async function handleCreateAlert() {
    if (!stop?.delivery_point_id || isCreatingAlert) return;
    const targetAudience = (alertDraft.target_audience || []).filter(Boolean);
    if (!alertDraft.alert_type || !targetAudience.length) {
      setError('Selecciona tipo de alerta y al menos un participante interno.');
      return;
    }
    setIsCreatingAlert(true);
    setError(null);
    try {
      const created = await createDeliveryAlert({
        delivery_point_id: stop.delivery_point_id,
        alert_type: alertDraft.alert_type,
        message: alertDraft.message?.trim() || null,
        target_audience: targetAudience,
        source: 'admin_driver_timeline',
      });
      setLocalAlertSummary(prev => applyUpdatedAlertToSummary(prev, created));
      onAlertUpdated?.(created);
      setAlertCreatorOpen(false);
      setAlertDraft(defaultAdminAlertDraft(stop));
    } catch (err) {
      setError(compactError(err, 'No se pudo crear la alerta operativa.'));
    } finally {
      setIsCreatingAlert(false);
    }
  }

  async function handleGuidanceAction(action) {
    if (!stop?.delivery_point_id || isUpdatingGuidance) return;
    setIsUpdatingGuidance(true);
    setError(null);
    try {
      const result = await recordDriverTimelineGuidance({
        delivery_point_id: stop.delivery_point_id,
        action,
        source: 'driver_timeline',
        target_type: selectedTarget?.target_type,
        conversation_id: activeConversationId,
      });
      if (result?.guidance_state) {
        setLocalGuidanceState(result.guidance_state);
      }
      if (action === 'customer_no_answer') {
        selectDraftPresetById('merchant_customer_no_answer', 'customer_not_answering');
      }
      if (action === 'customer_waiting') {
        selectDraftPresetById('driver_wait_5_min', 'wait_5_min');
      }
      if (action === 'store_notified' || action === 'store_no_answer') {
        selectDraftPresetById('merchant_driver_waiting', 'driver_waiting');
      }
      onGuidanceUpdated?.(result);
    } catch (err) {
      setError(compactError(err, 'No se pudo registrar la guía operativa.'));
    } finally {
      setIsUpdatingGuidance(false);
    }
  }

  function prepareAlertCustomerMessage() {
    const alertItem = activeAlertFromSummary(localAlertSummary);
    const target = localTargets.find(item => item.key === 'final_customer');
    if (!target) return;
    selectTarget(target);
    setSelectedDraftPresetId('customer_send_location');
    setSelectedIntent('request_reference');
    setAiDraftMeta(null);
    setDraft(
      alertItem?.alert_type === 'incorrect_location'
        ? 'Hola, tu motorizado esta cerca pero necesita confirmar la ubicacion exacta. Por favor envianos una referencia o tu ubicacion actual por WhatsApp.'
        : 'Hola, tu motorizado necesita confirmar algunos datos para completar la entrega. Por favor ayudanos respondiendo este mensaje.'
    );
  }

  function prepareAlertStoreMessage() {
    const alertItem = activeAlertFromSummary(localAlertSummary);
    const target = localTargets.find(item => item.key === 'store');
    if (!target) return;
    selectTarget(target);
    setSelectedDraftPresetId(alertItem?.alert_type === 'incorrect_location' ? 'merchant_location_changed' : 'merchant_customer_no_answer');
    setSelectedIntent(alertItem?.alert_type === 'incorrect_location' ? 'customer_location_changed' : 'customer_not_answering');
    setAiDraftMeta(null);
    setDraft(
      alertItem?.alert_type === 'incorrect_location'
        ? `La entrega #${stop.delivery_point_id} tiene alerta de ubicacion incorrecta. ¿Nos ayudas confirmando direccion/referencia correcta con tu cliente?`
        : `La entrega #${stop.delivery_point_id} tiene una alerta activa (${getAlertLabel(alertItem?.alert_type)}). ¿Nos ayudas a resolverla con tu cliente?`
    );
  }

  function handleKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if ((!draft.trim() && !mediaAttachment) || isSending || isAIWorking) return;
    handleSend(event);
  }

  const money = formatMoney(stop?.charge ?? stop?.charged);
  const activeAlert = activeAlertFromSummary(localAlertSummary);
  const activeAlertClasses = alertSeverityClasses(activeAlert?.severity);
  const storeTargetAvailable = localTargets.some(target => target.key === 'store');
  const guidanceClasses = guidanceStateClasses(localGuidanceState?.readiness_status);
  const pricingSnapshot = stop?.pricing_snapshot || {};
  const pricingFlags = stop?.pricing_flags || {};
  const pricingMoney = formatMoney(pricingSnapshot.pricing_total);

  if (!stop) return null;

  return (
    <div className="mt-3 rounded-2xl border border-blue-200 bg-blue-50/50 p-3 text-left shadow-inner">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-black text-slate-950">Entrega #{stop.delivery_point_id}</span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-extrabold text-slate-600">
              Seq {stop.sequence || '-'}
            </span>
            {stop.district && (
              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-extrabold text-blue-700">
                {stop.district}
              </span>
            )}
            {money && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-extrabold text-emerald-700">
                <CreditCard className="h-3 w-3" /> {money}
              </span>
            )}
            {pricingMoney && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-extrabold ${
                pricingFlags.is_enhanced ? 'bg-fuchsia-50 text-fuchsia-700' : 'bg-white text-slate-600'
              }`}>
                <Sparkles className="h-3 w-3" /> Precio {pricingMoney}
              </span>
            )}
            {pricingFlags.is_admin_adjusted && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-extrabold text-amber-700">
                Ajuste admin
              </span>
            )}
          </div>
          <p className="mt-1 line-clamp-1 text-xs font-semibold text-slate-500">{stop.address || 'Sin direccion'}</p>
        </div>
        <button
          type="button"
          onClick={() => onOpenFullDelivery?.(stop.delivery_point_id)}
          disabled={isOpening}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-extrabold text-[#B1336E] shadow-sm hover:bg-[#B1336E]/5 disabled:opacity-50"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Abrir chat completo
        </button>
      </div>

      <div className="mb-3 rounded-xl border border-slate-200 bg-white p-2 text-xs">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="font-extrabold text-slate-900">Pre-check operativo</span>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${guidanceClasses}`}>
            {localGuidanceState?.readiness_label || 'Sin revisar'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {GUIDANCE_ACTIONS.map(([action, label]) => {
            const active = guidanceActionIsActive(action, localGuidanceState);
            return (
              <button
                key={action}
                type="button"
                onClick={() => handleGuidanceAction(action)}
                disabled={isUpdatingGuidance}
                className={`rounded-full px-2 py-1 text-[10px] font-extrabold transition disabled:opacity-50 ${
                  active
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[10px] font-semibold text-slate-500">
          Estas acciones registran trazabilidad operativa; no envían mensajes ni cambian el estado de la entrega.
        </p>
      </div>


      <div className="mb-3 rounded-xl border border-rose-100 bg-white p-2 text-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="font-extrabold text-slate-900">Alertas internas</span>
            <p className="text-[10px] font-semibold text-slate-500">
              Crea una alerta visible solo para motorizado, tienda o proveedor seleccionados.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAlertCreatorOpen(prev => !prev)}
            className="inline-flex items-center gap-1 rounded-lg bg-[#B1336E] px-2.5 py-1.5 text-[11px] font-extrabold text-white hover:bg-[#9d2d62]"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {alertCreatorOpen ? 'Cerrar' : 'Crear alerta'}
          </button>
        </div>

        {alertCreatorOpen && (
          <div className="mt-3 space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-2">
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase text-slate-500">Tipo</span>
                <select
                  value={alertDraft.alert_type}
                  onChange={event => setAlertDraft(prev => ({ ...prev, alert_type: event.target.value }))}
                  className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-bold text-slate-700 outline-none focus:border-[#B1336E]"
                >
                  {alertTypeOptions.map(item => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="space-y-1">
                <span className="text-[10px] font-black uppercase text-slate-500">Destinatarios internos</span>
                <div className="flex flex-wrap gap-1.5">
                  {alertTargetOptions.map(option => {
                    const selected = (alertDraft.target_audience || []).includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleAlertTarget(option.value)}
                        className={`rounded-full px-2 py-1 text-[10px] font-extrabold ${
                          selected
                            ? 'bg-slate-900 text-white'
                            : 'bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {alertMessageSuggestions.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] font-black uppercase text-slate-500">Mensajes sugeridos</span>
                <div className="flex flex-wrap gap-1.5">
                  {alertMessageSuggestions.map(item => (
                    <button
                      key={`${item.targetType}-${item.text}`}
                      type="button"
                      onClick={() => setAlertDraft(prev => ({ ...prev, message: item.text }))}
                      className="rounded-full bg-white px-2 py-1 text-left text-[10px] font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-700"
                      title={item.text}
                    >
                      {TARGET_LABELS[item.targetType] || item.targetType}: usar sugerencia
                    </button>
                  ))}
                </div>
              </div>
            )}

            <textarea
              value={alertDraft.message}
              onChange={event => setAlertDraft(prev => ({ ...prev, message: event.target.value }))}
              rows={2}
              placeholder="Detalle operativo de la alerta..."
              className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[10px] font-semibold text-slate-500">
                No se envia a cliente final. Queda registrado como evento operacional.
              </span>
              <button
                type="button"
                onClick={handleCreateAlert}
                disabled={isCreatingAlert || !(alertDraft.target_audience || []).length}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-extrabold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {isCreatingAlert ? 'Creando...' : 'Guardar alerta'}
              </button>
            </div>
          </div>
        )}
      </div>

      {activeAlert && (
        <div className={`mb-3 rounded-xl border p-3 text-xs ${activeAlertClasses.card}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black ${activeAlertClasses.badge}`}>
                  <AlertTriangle className="h-3 w-3" />
                  {localAlertSummary?.active_count || 1} alerta{(localAlertSummary?.active_count || 1) > 1 ? 's' : ''}
                </span>
                <span className="font-extrabold text-slate-950">{getAlertLabel(activeAlert.alert_type)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${activeAlertClasses.soft}`}>
                  {activeAlert.status === 'acknowledged' ? 'En gestion' : 'Nueva'}
                </span>
              </div>
              <p className="mt-1 font-semibold text-slate-600">
                {getAlertDescription(activeAlert.alert_type)}
              </p>
              {activeAlert.message && (
                <p className="mt-1 rounded-lg bg-white/70 px-2 py-1 font-bold text-slate-800">
                  {activeAlert.message}
                </p>
              )}
              <p className="mt-1 text-[10px] font-semibold text-slate-500">
                Reportada {formatAlertTime(activeAlert.created_at)}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {activeAlert.status === 'open' && (
                <button
                  type="button"
                  onClick={() => handleAlertAction('acknowledge')}
                  disabled={isUpdatingAlert}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-2 py-1 text-[10px] font-black text-white disabled:opacity-50"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Reconocer
                </button>
              )}
              <button
                type="button"
                onClick={() => handleAlertAction('resolve')}
                disabled={isUpdatingAlert}
                className="rounded-lg bg-emerald-600 px-2 py-1 text-[10px] font-black text-white disabled:opacity-50"
              >
                Resolver
              </button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={prepareAlertCustomerMessage}
              className="rounded-full bg-white px-2 py-1 text-[10px] font-extrabold text-slate-700 hover:bg-slate-100"
            >
              Pedir ubicacion/ref.
            </button>
            <button
              type="button"
              onClick={prepareAlertStoreMessage}
              disabled={!storeTargetAvailable}
              className="rounded-full bg-white px-2 py-1 text-[10px] font-extrabold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Avisar tienda
            </button>
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-1.5">
        {localTargets.map(target => (
          <button
            key={target.key}
            type="button"
            onClick={() => selectTarget(target)}
            className={`rounded-full px-2.5 py-1.5 text-[11px] font-extrabold transition ${
              selectedKey === target.key
                ? 'bg-[#B1336E] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            {target.target_type === 'final_customer'
              ? (target.whatsapp_available ? 'Cliente WA' : 'Cliente App')
              : TARGET_LABELS[target.target_type] || target.label}
          </button>
        ))}
      </div>

      {selectedTarget?.contextOnly ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
          <div className="grid gap-2 sm:grid-cols-2">
            <div><span className="font-extrabold text-slate-900">Cliente:</span> {stop.recipient_name || 'Sin nombre'}</div>
            <div><span className="font-extrabold text-slate-900">Estado:</span> {stop.status || '-'}</div>
            <div><span className="font-extrabold text-slate-900">Tienda:</span> {stop.store_name || 'Sin tienda'}</div>
            <div><span className="font-extrabold text-slate-900">Proveedor:</span> {stop.provider_name || 'Sin proveedor'}</div>
            <div className="sm:col-span-2">
              <span className="font-extrabold text-slate-900">Direccion:</span> {stop.address || 'Sin direccion'}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-slate-500">
            <MapPin className="h-3.5 w-3.5" />
            Usa los chips de arriba para guiar al motorizado, contactar al cliente o avisar a tienda.
          </div>
        </div>
      ) : (
        <>
          <div className="mb-2 rounded-xl border border-slate-200 bg-white p-2 text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-extrabold text-slate-900">{selectedTarget?.label}</span>
              <span className="font-semibold text-slate-400">{selectedTarget?.phone_number || 'sin WhatsApp'}</span>
            </div>
            {selectedTarget?.whatsapp_disabled_reason && (
              <p className="mt-1 text-[11px] font-semibold text-amber-700">{selectedTarget.whatsapp_disabled_reason}</p>
            )}
          </div>

          <div className="mb-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setChannel(selectedTarget?.customer ? 'fastmax_app' : 'internal')}
              className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold ${
                channel !== 'whatsapp' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'
              }`}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {selectedTarget?.customer ? 'Fast Max App' : 'Interno'}
            </button>
            <button
              type="button"
              onClick={() => selectedTarget?.whatsapp_available && setChannel('whatsapp')}
              disabled={!selectedTarget?.whatsapp_available}
              className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 ${
                channel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" />
              WhatsApp
            </button>
          </div>

          <div className="mb-2 max-h-44 space-y-2 overflow-y-auto rounded-xl border border-slate-100 bg-white p-2">
            {isLoadingMessages && (
              <div className="flex items-center justify-center gap-2 p-3 text-xs font-semibold text-slate-500">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Cargando mensajes...
              </div>
            )}
            {!messages.length && !isLoadingMessages && (
              <div className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-500">
                Sin mensajes recientes para este objetivo.
              </div>
            )}
            {messages.map(message => (
              <MessageBubble key={`${message.channel}-${message.id}`} message={message} currentUserType="admin" />
            ))}
          </div>

          <div className="mb-2 rounded-xl border border-purple-100 bg-purple-50/60 p-2">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-black text-purple-900">Borradores rápidos</span>
              <span className="text-[10px] font-semibold text-purple-600">Selecciona el caso y luego AI draft.</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {draftPresets.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => selectDraftPreset(preset)}
                  className={`rounded-full px-2 py-1 text-[10px] font-extrabold ${
                    selectedDraftPreset?.id === preset.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-purple-100'
                  }`}
                  title={preset.instruction}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSend} className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleGenerateAIDraft}
                disabled={isAIWorking || isSending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-100 px-2.5 py-1.5 text-[11px] font-extrabold text-purple-700 hover:bg-purple-200 disabled:opacity-50"
              >
                <Bot className={`h-3.5 w-3.5 ${isAIWorking ? 'animate-pulse' : ''}`} />
                AI draft
              </button>
              <button
                type="button"
                onClick={handleImproveDraft}
                disabled={isAIWorking || isSending || !draft.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-100 px-2.5 py-1.5 text-[11px] font-extrabold text-sky-700 hover:bg-sky-200 disabled:opacity-50"
              >
                <Sparkles className={`h-3.5 w-3.5 ${isAIWorking ? 'animate-pulse' : ''}`} />
                Corregir
              </button>
              {selectedTarget && !selectedTarget.customer && (
                <button
                  type="button"
                  onClick={() => onOpenFullSupport?.(selectedTarget.scope, selectedTarget.user_id, selectedTarget.label)}
                  disabled={isOpening || !selectedTarget.user_id}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-extrabold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Abrir soporte
                </button>
              )}
              {aiDraftMeta && (
                <span className="rounded-full bg-purple-50 px-2 py-1 text-[10px] font-bold text-purple-700">
                  {aiDraftMeta.intent || 'draft'} {aiDraftMeta.confidence != null ? `${Math.round((aiDraftMeta.confidence || 0) * 100)}%` : ''}
                </span>
              )}
            </div>

            <textarea
              value={draft}
              onChange={event => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending || isAIWorking}
              rows={2}
              placeholder={`Mensaje para ${TARGET_LABELS[selectedTarget?.target_type] || selectedTarget?.target_type || 'objetivo'}...`}
              className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
            />
            <MediaMessageControls
              value={mediaAttachment}
              onChange={setMediaAttachment}
              disabled={isSending || isAIWorking}
              compact
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-slate-500">
                Enter envia · Shift+Enter nueva linea · Envio manual
              </span>
              <button
                type="submit"
                disabled={isSending || isAIWorking || (!draft.trim() && !mediaAttachment) || (channel === 'whatsapp' && !selectedTarget?.whatsapp_available)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#B1336E] px-3 py-2 text-xs font-bold text-white hover:bg-[#9d2d62] disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                Enviar
              </button>
            </div>
          </form>
        </>
      )}

      {error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-semibold text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
